import { ReceptionistCalendarStatus } from './ReceptionistCalendarStatus';
import { ReceptionistBookings } from './ReceptionistBookings';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Clock3,
  RotateCcw,
  Settings2,
  X,
} from "lucide-react";
import {
  completeSchedule,
  getAvailability,
  getAvailabilityBlocks,
  getCurrentUserId,
  manageAvailability,
  manageAvailabilityBlock,
  manageSchedule,
  manageMeeting,
} from "./lib/api";
import {
  getMeetingDirectory,
  type MeetingDirectoryUser,
} from "./lib/teamAccess";
import { uniqueMeetingDirectory } from "./lib/participantIdentity";
import type {
  Contact,
  Organization,
  ScheduleEvent,
  ScheduleEventType,
} from "./types";
import "./SchedulingPage.css";
import "./CalendarRefinement.css";

const types: ScheduleEventType[] = [
  "SALES_CALL",
  "DEMO",
  "FOLLOW_UP",
  "CUSTOMER_CALLBACK",
  "ONBOARDING",
  "CUSTOMER_SUCCESS",
  "SUPPORT_APPOINTMENT",
  "MEETING",
  "OTHER",
];
const localValue = (iso: string) => {
  const d = new Date(iso),
    offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
};
const futureDefault = () => {
  const d = new Date(Date.now() + 3600000);
  d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15, 0, 0);
  return localValue(d.toISOString());
};
const startDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();
const weekStart = (d: Date) => addDays(startDay(d), -d.getDay());
const humanType = (value: string) =>
  value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const eventContact = (e: ScheduleEvent, contacts: Contact[]) =>
  contacts.find((x) => x.id === e.contactId)?.name;
const eventCompany = (e: ScheduleEvent, organizations: Organization[]) =>
  organizations.find((x) => x.id === e.organizationId)?.name;
type View = "DAY" | "WEEK" | "MONTH" | "AGENDA";
type Scope = "MY" | "SALES" | "SUPPORT" | "SUCCESS" | "ONBOARDING";
type Form = {
  title: string;
  contactId: string;
  organizationId: string;
  opportunityId: string;
  eventType: ScheduleEventType;
  startsAt: string;
  duration: number;
  notes: string;
  reminderMinutes: string;
  meetingMode: "PHONE" | "VIDEO" | "AUDIO_CONFERENCE";
  meetingAudience: "INTERNAL" | "INVITE";
  externalEmails: string;
  timing: "NOW" | "SCHEDULED";
};
const empty = (): Form => ({
  title: "",
  contactId: "",
  organizationId: "",
  opportunityId: "",
  eventType: "MEETING",
  startsAt: futureDefault(),
  duration: 30,
  notes: "",
  reminderMinutes: "15",
  meetingMode: "PHONE",
  meetingAudience: "INTERNAL",
  externalEmails: "",
  timing: "SCHEDULED",
});

const idFragment = /\b[0-9a-f]{8}(?:-[0-9a-f]{4}){0,3}(?:-[0-9a-f]{8,12})?\b/i;
const displayEventTitle = (event: ScheduleEvent, contacts: Contact[]) => {
  const contact = eventContact(event, contacts),
    stored = event.title.trim();
  return idFragment.test(stored)
    ? `${humanType(event.eventType)}${contact ? ` · ${contact}` : ""}`
    : stored ||
        `${humanType(event.eventType)}${contact ? ` · ${contact}` : ""}`;
};
function EventButton({
  event,
  contacts,
  organizations,
  onClick,
  compact = false,
}: {
  event: ScheduleEvent;
  contacts: Contact[];
  organizations: Organization[];
  onClick: () => void;
  compact?: boolean;
}) {
  const start = new Date(event.startsAt),
    end = new Date(event.endsAt),
    label = displayEventTitle(event, contacts),
    contact = eventContact(event, contacts),
    company = eventCompany(event, organizations),
    detail = company && company !== contact ? company : contact,
    startLabel = start.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    }),
    endLabel = end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  return (
    <button
      className={`calendar-event ${compact ? "month-compact " : ""}${event.status.toLowerCase()} type-${event.eventType.toLowerCase()}`}
      onClick={onClick}
      title={`${startLabel} – ${endLabel} · ${label}${detail ? ` · ${detail}` : ""}`}
    >
      {compact ? (
        <span>
          {startLabel} · {label}
        </span>
      ) : (
        <>
          <b>
            {startLabel} – {endLabel}
          </b>
          <span>{label}</span>
          {detail && <small>{detail}</small>}
        </>
      )}
    </button>
  );
}
type TimedPlacement = {
  event: ScheduleEvent;
  dayIndex: number;
  top: number;
  height: number;
  lane: number;
  laneCount: number;
};
function timedPlacements(events: ScheduleEvent[], days: Date[]) {
  const placed: TimedPlacement[] = [];
  days.forEach((day, dayIndex) => {
    const items = events
      .filter((event) => event.status !== "CANCELLED")
      .map((event) => {
        const start = new Date(event.startsAt),
          end = new Date(event.endsAt),
          top = start.getHours() * 60 + start.getMinutes(),
          duration = Math.max(15, (end.getTime() - start.getTime()) / 60000),
          height = Math.max(28, duration);
        return { event, start, top, height, visualEnd: top + height };
      })
      .filter((item) => sameDay(item.start, day))
      .sort((a, b) => a.top - b.top);
    let cluster: typeof items = [],
      clusterEnd = -1;
    const commit = () => {
      if (!cluster.length) return;
      const laneEnds: number[] = [],
        assigned = cluster.map((item) => {
          let lane = laneEnds.findIndex((end) => end <= item.top);
          if (lane < 0) lane = laneEnds.length;
          laneEnds[lane] = item.visualEnd;
          return { ...item, lane };
        }),
        laneCount = Math.max(1, laneEnds.length);
      assigned.forEach((item) =>
        placed.push({
          event: item.event,
          dayIndex,
          top: item.top,
          height: item.height,
          lane: item.lane,
          laneCount,
        }),
      );
      cluster = [];
    };
    items.forEach((item) => {
      if (cluster.length && item.top >= clusterEnd) {
        commit();
        clusterEnd = -1;
      }
      cluster.push(item);
      clusterEnd = Math.max(clusterEnd, item.visualEnd);
    });
    commit();
  });
  return placed;
}
function TimeGrid({
  days,
  events,
  contacts,
  organizations,
  onSelect,
}: {
  days: Date[];
  events: ScheduleEvent[];
  contacts: Contact[];
  organizations: Organization[];
  onSelect: (e: ScheduleEvent) => void;
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i),
    shell = useRef<HTMLDivElement>(null),
    [now, setNow] = useState(new Date()),
    placements = useMemo(() => timedPlacements(events, days), [events, days]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!shell.current) return;
    const relevant = days.some((day) => sameDay(day, new Date())),
      minutes = relevant
        ? Math.max(
            0,
            new Date().getHours() * 60 + new Date().getMinutes() - 120,
          )
        : 420;
    shell.current.scrollTop = minutes;
  }, [days]);
  const todayIndex = days.findIndex((day) => sameDay(day, now)),
    nowMinutes = now.getHours() * 60 + now.getMinutes();
  return (
    <div className="calendar-time-shell" ref={shell}>
      <div
        className="calendar-time-grid"
        style={{ "--calendar-days": days.length } as React.CSSProperties}
      >
        <div className="calendar-corner" />
        {days.map((day) => (
          <div
            className={`calendar-day-heading ${sameDay(day, now) ? "today" : ""}`}
            key={day.toISOString()}
          >
            <b>{day.toLocaleDateString([], { weekday: "short" })}</b>
            <span>
              {day.toLocaleDateString([], { month: "short", day: "numeric" })}
            </span>
          </div>
        ))}
        {hours.map((hour) => (
          <div
            className="calendar-hour-row"
            key={hour}
            style={{ gridColumn: `1 / ${days.length + 2}`, gridRow: hour + 2 }}
          />
        ))}
        {hours.map((hour) => (
          <div
            className="calendar-hour-label"
            key={`l${hour}`}
            style={{ gridRow: hour + 2 }}
          >
            {new Date(2000, 0, 1, hour).toLocaleTimeString([], {
              hour: "numeric",
            })}
          </div>
        ))}
        {days.map((day, di) => (
          <div
            className={`calendar-day-track ${sameDay(day, now) ? "today" : ""}`}
            key={`t${di}`}
            style={{ gridColumn: di + 2, gridRow: "2 / 26" }}
          />
        ))}
        {todayIndex >= 0 && (
          <div
            className="calendar-now-indicator"
            aria-label={`Current time ${now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}
            style={{
              gridColumn: todayIndex + 2,
              gridRow: 2,
              top: `${nowMinutes}px`,
            }}
          >
            <i />
            <span>
              {now.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          </div>
        )}
        {placements.map(({ event, dayIndex, top, height, lane, laneCount }) => (
          <div
            className="calendar-positioned-event"
            key={event.id}
            style={{
              gridColumn: dayIndex + 2,
              gridRow: 2,
              top: `${top}px`,
              height: `${height}px`,
              left: `calc(${(lane / laneCount) * 100}% + 4px)`,
              width: `calc(${100 / laneCount}% - 8px)`,
            }}
          >
            <EventButton
              event={event}
              contacts={contacts}
              organizations={organizations}
              onClick={() => onSelect(event)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SchedulingPage({
  events,
  setEvents,
  contacts,
  organizations,
}: {
  events: ScheduleEvent[];
  setEvents: (value: ScheduleEvent[]) => void;
  contacts: Contact[];
  organizations: Organization[];
}) {
  const [open, setOpen] = useState(false),
    [editing, setEditing] = useState<ScheduleEvent | null>(null),
    [form, setForm] = useState<Form>(empty),
    [state, setState] = useState<"idle" | "saving" | "success" | "error">(
      "idle",
    ),
    [error, setError] = useState(""),
    [view, setView] = useState<View>("WEEK"),
    [scope, setScope] = useState<Scope>("MY"),
    [query, setQuery] = useState(""),
    [anchor, setAnchor] = useState(startDay(new Date())),
    [settings, setSettings] = useState(false),
    [params] = useSearchParams(),
    routeNavigate = useNavigate(),
    contextOpened = useRef(false);
  const [selected, setSelected] = useState<ScheduleEvent | null>(null);
  const [directory, setDirectory] = useState<MeetingDirectoryUser[]>([]);
  const [directoryQuery, setDirectoryQuery] = useState("");
  const [internalUserIds, setInternalUserIds] = useState<string[]>([]);
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [meetingGroups, setMeetingGroups] = useState<
    Array<"SALES" | "SUPPORT" | "OPERATIONS">
  >([]);
  const [allTeam, setAllTeam] = useState(false);
  const idempotencyKey = useRef(crypto.randomUUID());
  const [eventParam] = useState(() =>
    new URLSearchParams(location.search).get("event"),
  );
  useEffect(() => {
    if (eventParam) {
      const found = events.find((item) => item.id === eventParam);
      if (found) {
        setSelected(found);
        setAnchor(startDay(new Date(found.startsAt)));
      }
    }
  }, [eventParam, events]);
  useEffect(() => {
    if (!open) return;
    getMeetingDirectory()
      .then((value) => setDirectory(value.users))
      .catch(() => setDirectory([]));
  }, [open]);
  const scoped = useMemo(
    () =>
      events
        .filter(
          (e) =>
            (scope === "MY" ||
              (scope === "SALES" &&
                ["SALES_CALL", "DEMO", "FOLLOW_UP"].includes(e.eventType)) ||
              (scope === "SUPPORT" &&
                ["CUSTOMER_CALLBACK", "SUPPORT_APPOINTMENT"].includes(
                  e.eventType,
                )) ||
              (scope === "SUCCESS" && e.eventType === "CUSTOMER_SUCCESS") ||
              (scope === "ONBOARDING" && e.eventType === "ONBOARDING")) &&
            (!query.trim() ||
              [
                e.title,
                e.notes,
                eventContact(e, contacts),
                eventCompany(e, organizations),
                humanType(e.eventType),
              ].some((value) =>
                value?.toLowerCase().includes(query.trim().toLowerCase()),
              )),
        )
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [events, scope, query, contacts, organizations],
  );
  const days = useMemo(
    () =>
      view === "DAY"
        ? [anchor]
        : view === "WEEK"
          ? Array.from({ length: 7 }, (_, i) => addDays(weekStart(anchor), i))
          : [],
    [view, anchor],
  );
  const monthDays = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1),
      start = weekStart(first);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [anchor]);
  const rangeLabel =
    view === "DAY"
      ? anchor.toLocaleDateString([], {
          weekday: "long",
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : view === "WEEK"
        ? `${days[0]?.toLocaleDateString([], { month: "short", day: "numeric" })} – ${days[6]?.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`
        : view === "MONTH"
          ? anchor.toLocaleDateString([], { month: "long", year: "numeric" })
          : "Scheduled work";
  function navigate(direction: number) {
    if (view === "DAY") setAnchor(addDays(anchor, direction));
    else if (view === "WEEK" || view === "AGENDA")
      setAnchor(addDays(anchor, 7 * direction));
    else
      setAnchor(
        new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1),
      );
  }
  function begin(event?: ScheduleEvent, start?: Date) {
    idempotencyKey.current = crypto.randomUUID();
    setInternalUserIds([]);
    setContactIds(event?.contactId ? [event.contactId] : []);
    setMeetingGroups([]);
    setAllTeam(false);
    setDirectoryQuery("");
    setEditing(event || null);
    setForm(
      event
        ? {
            title: event.title,
            contactId: event.contactId || "",
            organizationId: event.organizationId || "",
            opportunityId: event.opportunityId || "",
            eventType: event.eventType,
            startsAt: localValue(event.startsAt),
            duration: Math.max(
              5,
              Math.round(
                (new Date(event.endsAt).getTime() -
                  new Date(event.startsAt).getTime()) /
                  60000,
              ),
            ),
            notes: event.notes,
            reminderMinutes:
              event.reminderMinutes === undefined
                ? ""
                : String(event.reminderMinutes),
            meetingMode: event.meetingMode || "PHONE",
            meetingAudience:
              event.meetingAudience === "INVITE" ||
              event.meetingAudience === "CONTACT" ||
              event.meetingAudience === "EXTERNAL"
                ? "INVITE"
                : "INTERNAL",
            externalEmails: "",
            timing: "SCHEDULED",
          }
        : {
            ...empty(),
            startsAt: start ? localValue(start.toISOString()) : futureDefault(),
          },
    );
    setState("idle");
    setError("");
    setOpen(true);
  }
  useEffect(() => {
    if (contextOpened.current) return;
    const contactId = params.get("contact") || "",
      organizationId = params.get("organization") || "",
      opportunityId = params.get("opportunity") || "",
      eventType = params.get("type") as ScheduleEventType | null,
      title = params.get("title") || "";
    const meeting = params.get("meeting");
    if (!contactId && !organizationId && meeting !== "new") return;
    contextOpened.current = true;
    idempotencyKey.current = crypto.randomUUID();
    setInternalUserIds([]);
    setContactIds(contactId ? [contactId] : []);
    setMeetingGroups([]);
    setAllTeam(false);
    setEditing(null);
    setForm({
      ...empty(),
      contactId,
      organizationId,
      opportunityId,
      title:
        title || (meeting === "new" ? "Team meeting" : "Customer callback"),
      eventType:
        meeting === "new"
          ? "MEETING"
          : eventType && types.includes(eventType)
            ? eventType
            : "CUSTOMER_CALLBACK",
      meetingMode: meeting === "new" ? "VIDEO" : "PHONE",
    });
    setState("idle");
    setError("");
    setOpen(true);
    routeNavigate("/calendar", { replace: true });
  }, [params, routeNavigate]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (state === "saving") return;
    const start =
      form.timing === "NOW"
        ? new Date(Date.now() + 120000)
        : new Date(form.startsAt);
    if (
      !form.title.trim() ||
      Number.isNaN(start.getTime()) ||
      start <= new Date() ||
      form.duration < 5
    ) {
      setState("error");
      setError(
        "Enter a title, a future start time, and a duration of at least five minutes.",
      );
      return;
    }
    setState("saving");
    setError("");
    try {
      const userId = editing?.assignedUserId || (await getCurrentUserId()),
        savedSchedule = await manageSchedule({
          action: editing ? "UPDATE" : "CREATE",
          eventId: editing?.id || crypto.randomUUID(),
          contactId: form.contactId || null,
          organizationId: form.organizationId || null,
          opportunityId: form.opportunityId || null,
          assignedUserId: userId,
          title: form.title,
          eventType: form.eventType,
          startsAt: start.toISOString(),
          endsAt: new Date(
            start.getTime() + form.duration * 60000,
          ).toISOString(),
          notes: form.notes,
          reminderMinutes:
            form.reminderMinutes === "" ? null : Number(form.reminderMinutes),
        }),
        externalEmails = [
          ...new Set(
            form.externalEmails
              .split(/[;,\n]/)
              .map((value) => value.trim().toLowerCase())
              .filter(Boolean),
          ),
        ];
      if (externalEmails.some((email) => !/^\S+@\S+\.\S+$/.test(email)))
        throw new Error("invalid_external_email");
      const meeting = await manageMeeting({
        action:
          form.meetingMode === "PHONE"
            ? "PHONE"
            : editing?.providerEventId
              ? "UPDATE"
              : "CREATE",
        eventId: savedSchedule.id,
        idempotencyKey: idempotencyKey.current,
        meetingMode: form.meetingMode,
        audience: form.meetingAudience,
        participantSpec: {
          internalUserIds,
          contactIds,
          externalEmails,
          groups: meetingGroups,
          allTeam,
        },
        opportunityId: form.opportunityId || null,
        event: {
          title: form.title.trim(),
          start_time: start.toISOString(),
          end_time: new Date(
            start.getTime() + form.duration * 60000,
          ).toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          description: form.notes,
        },
      });
      const saved = meeting.event;
      setEvents([saved, ...events.filter((item) => item.id !== saved.id)]);
      setSelected(saved);
      setAnchor(startDay(start));
      setState("success");
      setTimeout(() => setOpen(false), 350);
    } catch (err) {
      setState("error");
      setError(
        err instanceof Error && err.message === "schedule_conflict"
          ? "That time is unavailable because it overlaps another scheduled event. Choose a different time."
          : err instanceof Error
            ? err.message.replaceAll("_", " ")
            : "Schedule could not be saved.",
      );
    }
  }
  async function change(action: "CANCEL" | "COMPLETE") {
    if (!selected || state === "saving") return;
    if (
      action === "CANCEL" &&
      !confirm("Cancel this event? Its history will be preserved.")
    )
      return;
    setState("saving");
    setError("");
    try {
      if (action === "CANCEL")
        await manageMeeting({
          action: "CANCEL",
          eventId: selected.id,
          idempotencyKey: crypto.randomUUID(),
          meetingMode: selected.meetingMode || "PHONE",
          audience:
            selected.meetingAudience === "INVITE" ? "INVITE" : "INTERNAL",
          participantSpec: {
            internalUserIds: [],
            contactIds: [],
            externalEmails: [],
            groups: [],
            allTeam: false,
          },
        });
      const saved =
        action === "COMPLETE"
          ? await completeSchedule(selected.id)
          : await manageSchedule({
              action: "CANCEL",
              eventId: selected.id,
              contactId: selected.contactId || null,
              organizationId: selected.organizationId || null,
              assignedUserId: selected.assignedUserId,
              title: selected.title,
              eventType: selected.eventType,
              startsAt: selected.startsAt,
              endsAt: selected.endsAt,
              notes: selected.notes,
              reminderMinutes: selected.reminderMinutes ?? null,
            });
      setEvents(events.map((x) => (x.id === saved.id ? saved : x)));
      setSelected(saved);
      setState("success");
    } catch (err) {
      setState("error");
      setError(
        err instanceof Error
          ? err.message.replaceAll("_", " ")
          : "Event update failed.",
      );
    }
  }
  return (
    <div className="page persistent-calendar-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">CALENDAR</span>
          <h1>CRM Calendar</h1>
          <p>
            Canonical scheduling for appointments, callbacks, follow-ups, and
            internal work.
          </p>
        </div>
        <div className="calendar-primary-actions">
          <button onClick={() => setSettings(true)}>
            <Settings2 size={15} />
            Availability
          </button>
          <button className="primary-btn" onClick={() => begin()}>
            <CalendarPlus size={16} />
            Schedule
          </button>
        </div>
      </div>
      <div className="calendar-toolbar">
        <div className="calendar-scope-tabs">
          <button
            className={scope === "MY" ? "active" : ""}
            onClick={() => setScope("MY")}
          >
            My Calendar
          </button>
          <select
            aria-label="Shared calendar"
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
          >
            <option value="MY">Shared Calendars</option>
            <option value="SALES">Sales</option>
            <option value="SUPPORT">Support / Callbacks</option>
            <option value="SUCCESS">Customer Success</option>
            <option value="ONBOARDING">Onboarding</option>
          </select>
          <input
            aria-label="Search calendar"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search calendar"
          />
          <button
            onClick={() => {
              setQuery("");
              setScope("MY");
            }}
          >
            Clear filters
          </button>
        </div>
        <div className="calendar-nav">
          <button aria-label="Previous" onClick={() => navigate(-1)}>
            <ChevronLeft />
          </button>
          <button onClick={() => setAnchor(startDay(new Date()))}>Today</button>
          <button aria-label="Next" onClick={() => navigate(1)}>
            <ChevronRight />
          </button>
          <strong>{rangeLabel}</strong>
        </div>
        <div className="calendar-view-tabs">
          {(["DAY", "WEEK", "MONTH", "AGENDA"] as const).map((item) => (
            <button
              className={view === item ? "active" : ""}
              onClick={() => setView(item)}
              key={item}
            >
              {item[0] + item.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>
      <ReceptionistCalendarStatus />
      <ReceptionistBookings />
      {view === "DAY" || view === "WEEK" ? (
        <TimeGrid
          days={days}
          events={scoped}
          contacts={contacts}
          organizations={organizations}
          onSelect={setSelected}
        />
      ) : view === "MONTH" ? (
        <div className="calendar-month-grid">
          {monthDays.map((day) => {
            const list = scoped.filter((e) =>
              sameDay(new Date(e.startsAt), day),
            );
            return (
              <section
                className={`${day.getMonth() !== anchor.getMonth() ? "outside " : ""}${sameDay(day, new Date()) ? "today" : ""}`}
                key={day.toISOString()}
                onDoubleClick={() => {
                  const start = new Date(day);
                  start.setHours(9);
                  begin(undefined, start);
                }}
              >
                <header>{day.getDate()}</header>
                {list.slice(0, 3).map((e) => (
                  <EventButton
                    compact
                    event={e}
                    contacts={contacts}
                    organizations={organizations}
                    onClick={() => setSelected(e)}
                    key={e.id}
                  />
                ))}
                {list.length > 3 && (
                  <button
                    className="calendar-more"
                    onClick={() => {
                      setAnchor(day);
                      setView("AGENDA");
                    }}
                  >
                    +{list.length - 3} more
                  </button>
                )}
              </section>
            );
          })}
        </div>
      ) : (
        <section className="panel schedule-list">
          <h2>Agenda</h2>
          {scoped.length === 0 ? (
            <div className="schedule-empty">No scheduled items.</div>
          ) : (
            scoped.map((item) => (
              <button
                key={item.id}
                className={`schedule-row ${item.status.toLowerCase()}`}
                onClick={() => setSelected(item)}
              >
                <Clock3 size={16} />
                <div>
                  <strong>{item.title}</strong>
                  <span>
                    {new Date(item.startsAt).toLocaleString()} ·{" "}
                    {humanType(item.eventType)}
                  </span>
                </div>
                <b>{item.status}</b>
              </button>
            ))
          )}
        </section>
      )}
      {selected && (
        <aside className="schedule-detail">
          <header>
            <div>
              <span>{humanType(selected.eventType)}</span>
              <h2>{selected.title}</h2>
            </div>
            <button
              aria-label="Close schedule details"
              onClick={() => setSelected(null)}
            >
              <X />
            </button>
          </header>
          <div className="schedule-detail-body">
            <p>
              <strong>Status:</strong> {selected.status}
            </p>
            <p>
              <strong>Date:</strong>{" "}
              {new Date(selected.startsAt).toLocaleDateString()}
            </p>
            <p>
              <strong>Time:</strong>{" "}
              {new Date(selected.startsAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}{" "}
              –{" "}
              {new Date(selected.endsAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </p>
            <p>
              <strong>Timezone:</strong>{" "}
              {Intl.DateTimeFormat().resolvedOptions().timeZone}
            </p>
            <p>
              <strong>Format:</strong>{" "}
              {humanType(selected.meetingMode || "PHONE")}
            </p>
            <p>
              <strong>Provider:</strong>{" "}
              {selected.meetingProvider === "ZOHO_CLIQ"
                ? "Cliq"
                : selected.meetingProvider === "ZOOM"
                  ? "Zoom · historical"
                  : "Native phone"}
            </p>
            {selected.meetingProviderStatus && (
              <p>
                <strong>Meeting state:</strong>{" "}
                {humanType(selected.meetingProviderStatus)}
              </p>
            )}
            {selected.externalGuestAccessState === "SETUP_REQUIRED" && (
              <div className="runtime-banner degraded">
                <div>
                  <strong>Host action required</strong>
                  <span>
                    Open this event in Cliq and enable external guests before
                    sharing the join path. The attendee list and CRM event are
                    preserved.
                  </span>
                </div>
                {selected.joinUrl && (
                  <a href={selected.joinUrl} target="_blank" rel="noreferrer">
                    Open in Cliq
                  </a>
                )}
              </div>
            )}
            {selected.joinUrl && (
              <div className="schedule-meeting-actions">
                <a
                  className="primary-btn"
                  href={selected.joinUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Join meeting
                </a>
                <button
                  type="button"
                  onClick={() =>
                    navigator.clipboard.writeText(selected.joinUrl || "")
                  }
                >
                  Copy join link
                </button>
              </div>
            )}
            <p>
              <strong>Reminder:</strong>{" "}
              {selected.reminderMinutes === undefined
                ? "None"
                : `${selected.reminderMinutes} minutes before · in-app intent`}
            </p>
            {selected.notes && (
              <p>
                <strong>Notes:</strong> {selected.notes}
              </p>
            )}
            {selected.contactId && (
              <a href={`/contacts?contact=${selected.contactId}`}>
                Open Contact
              </a>
            )}
            {selected.organizationId && (
              <a
                href={`/organizations?organization=${selected.organizationId}`}
              >
                Open Company
              </a>
            )}
            <RecordEventNote event={selected} />
          </div>
          <footer>
            <button
              className="secondary-btn"
              disabled={selected.status !== "SCHEDULED" || state === "saving"}
              onClick={() => begin(selected)}
            >
              <RotateCcw size={14} />
              Edit / Reschedule
            </button>
            <button
              disabled={selected.status !== "SCHEDULED" || state === "saving"}
              onClick={() => change("COMPLETE")}
            >
              Complete
            </button>
            <button
              className="danger-btn"
              disabled={selected.status !== "SCHEDULED" || state === "saving"}
              onClick={() => change("CANCEL")}
            >
              Cancel
            </button>
          </footer>
          {state === "error" && (
            <div className="composer-error" role="alert">
              {error}
            </div>
          )}
        </aside>
      )}
      {settings && <AvailabilityPanel onClose={() => setSettings(false)} />}
      {open && (
        <div className="record-modal-backdrop">
          <form
            className="record-modal schedule-modal"
            noValidate
            onSubmit={save}
          >
            <header>
              <div>
                <span className="eyebrow">
                  {editing ? "EDIT EVENT" : "NEW EVENT"}
                </span>
                <h2>{editing ? "Edit or reschedule" : "Schedule"}</h2>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="schedule-form-grid">
              <label>
                Title / purpose
                <input
                  autoFocus
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </label>
              <label>
                Type
                <select
                  value={form.eventType}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      eventType: e.target.value as ScheduleEventType,
                    })
                  }
                >
                  {types.map((type) => (
                    <option key={type} value={type}>
                      {humanType(type)}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="meeting-segment">
                <legend>Meeting</legend>
                <button
                  type="button"
                  className={
                    form.meetingAudience === "INTERNAL" ? "active" : ""
                  }
                  onClick={() =>
                    setForm({ ...form, meetingAudience: "INTERNAL" })
                  }
                >
                  Team
                </button>
                <button
                  type="button"
                  className={form.meetingAudience === "INVITE" ? "active" : ""}
                  onClick={() =>
                    setForm({ ...form, meetingAudience: "INVITE" })
                  }
                >
                  Invite
                </button>
              </fieldset>
              <fieldset className="meeting-segment">
                <legend>When</legend>
                <button
                  type="button"
                  disabled={Boolean(editing)}
                  className={form.timing === "NOW" ? "active" : ""}
                  onClick={() => setForm({ ...form, timing: "NOW" })}
                >
                  Meet now
                </button>
                <button
                  type="button"
                  className={form.timing === "SCHEDULED" ? "active" : ""}
                  onClick={() => setForm({ ...form, timing: "SCHEDULED" })}
                >
                  Schedule
                </button>
              </fieldset>
              <fieldset className="meeting-segment">
                <legend>Format · Cliq</legend>
                <button
                  type="button"
                  className={form.meetingMode === "VIDEO" ? "active" : ""}
                  onClick={() => setForm({ ...form, meetingMode: "VIDEO" })}
                >
                  Video
                </button>
                <button
                  type="button"
                  className={
                    form.meetingMode === "AUDIO_CONFERENCE" ? "active" : ""
                  }
                  onClick={() =>
                    setForm({ ...form, meetingMode: "AUDIO_CONFERENCE" })
                  }
                >
                  Audio
                </button>
                <button
                  type="button"
                  className={form.meetingMode === "PHONE" ? "active" : ""}
                  onClick={() => setForm({ ...form, meetingMode: "PHONE" })}
                >
                  Phone
                </button>
              </fieldset>
              <label>
                Start
                <input
                  type="datetime-local"
                  disabled={form.timing === "NOW"}
                  value={form.startsAt}
                  onChange={(e) =>
                    setForm({ ...form, startsAt: e.target.value })
                  }
                />
              </label>
              <label>
                Duration
                <select
                  value={form.duration}
                  onChange={(e) =>
                    setForm({ ...form, duration: Number(e.target.value) })
                  }
                >
                  {[15, 30, 45, 60, 90, 120].map((value) => (
                    <option key={value} value={value}>
                      {value} minutes
                    </option>
                  ))}
                </select>
              </label>
              {form.meetingAudience === "INVITE" && (
                <label>
                  Contact
                  <select
                    value={form.contactId}
                    onChange={(e) => {
                      const contact = contacts.find(
                          (item) => item.id === e.target.value,
                        ),
                        org = organizations.find(
                          (item) => item.name === contact?.company,
                        );
                      setForm({
                        ...form,
                        contactId: e.target.value,
                        organizationId: org?.id || form.organizationId,
                        title:
                          form.title ||
                          `${humanType(form.eventType)} — ${contact?.name || ""}`,
                      });
                    }}
                  >
                    <option value="">No contact</option>
                    {contacts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {form.meetingAudience === "INVITE" && (
                <label>
                  Company
                  <select
                    value={form.organizationId}
                    onChange={(e) =>
                      setForm({ ...form, organizationId: e.target.value })
                    }
                  >
                    <option value="">No company</option>
                    {organizations.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Reminder
                <select
                  value={form.reminderMinutes}
                  onChange={(e) =>
                    setForm({ ...form, reminderMinutes: e.target.value })
                  }
                >
                  <option value="">None</option>
                  <option value="0">At start</option>
                  <option value="15">15 minutes before</option>
                  <option value="60">1 hour before</option>
                  <option value="1440">1 day before</option>
                </select>
              </label>
              {form.meetingMode !== "PHONE" && (
                <section
                  className="meeting-participant-picker wide"
                  aria-label="Meeting participants"
                >
                  <div className="participant-picker-heading">
                    <strong>
                      {form.meetingAudience === "INTERNAL"
                        ? "Team participants"
                        : "Invite people"}
                    </strong>
                    <small>
                      {internalUserIds.length +
                        contactIds.length +
                        (form.externalEmails.trim()
                          ? form.externalEmails.split(/[;,\n]/).filter(Boolean)
                              .length
                          : 0)}{" "}
                      selected
                    </small>
                  </div>
                  <div className="participant-group-row">
                    <button
                      type="button"
                      className={allTeam ? "active" : ""}
                      onClick={() => setAllTeam(!allTeam)}
                    >
                      All Team
                    </button>
                    {(["SALES", "SUPPORT", "OPERATIONS"] as const).map(
                      (group) => (
                        <button
                          type="button"
                          className={
                            meetingGroups.includes(group) ? "active" : ""
                          }
                          key={group}
                          onClick={() =>
                            setMeetingGroups(
                              meetingGroups.includes(group)
                                ? meetingGroups.filter((item) => item !== group)
                                : [...meetingGroups, group],
                            )
                          }
                        >
                          {group[0] + group.slice(1).toLowerCase()}
                        </button>
                      ),
                    )}
                  </div>
                  <input
                    aria-label="Search people"
                    placeholder="Search people"
                    value={directoryQuery}
                    onChange={(e) => setDirectoryQuery(e.target.value)}
                  />
                  <div
                    className="participant-options"
                    role="listbox"
                    aria-label="Internal people"
                  >
                    {uniqueMeetingDirectory(directory)
                      .filter((user) =>
                        `${user.firstName} ${user.lastName} ${user.email}`
                          .toLowerCase()
                          .includes(directoryQuery.toLowerCase()),
                      )
                      .map((user) => (
                        <button
                          type="button"
                          className={
                            internalUserIds.includes(user.id) ? "selected" : ""
                          }
                          key={user.id}
                          onClick={() =>
                            setInternalUserIds(
                              internalUserIds.includes(user.id)
                                ? internalUserIds.filter((id) => id !== user.id)
                                : [...internalUserIds, user.id],
                            )
                          }
                        >
                          <span>
                            {user.firstName} {user.lastName}
                            <small>{user.role}</small>
                          </span>
                          {internalUserIds.includes(user.id) ? "✓" : "+"}
                        </button>
                      ))}
                  </div>
                  {form.meetingAudience === "INVITE" && (
                    <>
                      <div className="participant-contact-results">
                        <strong>CRM contacts</strong>
                        {contacts
                          .filter(
                            (contact) =>
                              contact.email &&
                              !/\b(test|qa|synthetic|fixture)\b/i.test(
                                `${contact.name} ${contact.email}`,
                              ) &&
                              `${contact.name} ${contact.email}`
                                .toLowerCase()
                                .includes(directoryQuery.toLowerCase()),
                          )
                          .slice(0, 8)
                          .map((contact) => (
                            <button
                              type="button"
                              className={
                                contactIds.includes(contact.id)
                                  ? "selected"
                                  : ""
                              }
                              key={contact.id}
                              onClick={() =>
                                setContactIds(
                                  contactIds.includes(contact.id)
                                    ? contactIds.filter(
                                        (id) => id !== contact.id,
                                      )
                                    : [...contactIds, contact.id],
                                )
                              }
                            >
                              {contact.name}
                              <small>{contact.email}</small>
                              {contactIds.includes(contact.id) ? "✓" : "+"}
                            </button>
                          ))}
                      </div>
                      <label>
                        External guests
                        <textarea
                          value={form.externalEmails}
                          onChange={(e) =>
                            setForm({ ...form, externalEmails: e.target.value })
                          }
                          placeholder="guest@example.com"
                        />
                      </label>
                    </>
                  )}
                  <div className="participant-summary" role="status">
                    {allTeam
                      ? "All Team"
                      : `${internalUserIds.length} teammate${internalUserIds.length === 1 ? "" : "s"}`}{" "}
                    · {meetingGroups.length} group
                    {meetingGroups.length === 1 ? "" : "s"} ·{" "}
                    {contactIds.length} CRM contact
                    {contactIds.length === 1 ? "" : "s"}
                  </div>
                  {form.meetingAudience === "INVITE" && (
                    <small>
                      Cliq creates the provider meeting and retains attendees.
                      If external access still needs a host toggle, use Open in
                      Cliq from the saved event.
                    </small>
                  )}
                </section>
              )}
              <label className="wide">
                Notes
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </label>
            </div>
            <p className="schedule-provider-note">
              Phone stays in the native call workflow. Video and audio
              conferences use the connected Cliq account. Email invitations are
              not sent without separate authorization.
            </p>
            {state === "error" && (
              <div className="composer-error" role="alert">
                {error}
              </div>
            )}
            {state === "success" && (
              <div className="composer-success" role="status">
                Event saved.
              </div>
            )}
            <footer>
              <button type="button" onClick={() => setOpen(false)}>
                Close
              </button>
              <button className="primary-btn" disabled={state === "saving"}>
                {state === "saving"
                  ? "Saving…"
                  : editing
                    ? "Save changes"
                    : "Create event"}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}

function RecordEventNote({ event }: { event: ScheduleEvent }) {
  return (
    <div className="calendar-event-evidence">
      <small>Event ID</small>
      <code>{event.id}</code>
      <span>Updates retain this canonical event identity.</span>
    </div>
  );
}
function AvailabilityPanel({ onClose }: { onClose: () => void }) {
  const [timezone, setTimezone] = useState(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    ),
    [bufferBefore, setBufferBefore] = useState(15),
    [bufferAfter, setBufferAfter] = useState(15),
    [notice, setNotice] = useState(60),
    [horizon, setHorizon] = useState(60),
    [hours, setHours] = useState<
      Record<string, { start: string; end: string } | null>
    >(() =>
      Object.fromEntries(
        ["mon", "tue", "wed", "thu", "fri"].map((day) => [
          day,
          { start: "09:00", end: "17:00" },
        ]),
      ),
    ),
    [blocks, setBlocks] = useState<
      Awaited<ReturnType<typeof getAvailabilityBlocks>>
    >([]),
    [blockStart, setBlockStart] = useState(futureDefault()),
    [blockEnd, setBlockEnd] = useState(() =>
      localValue(new Date(Date.now() + 7200000).toISOString()),
    ),
    [blockReason, setBlockReason] = useState("Unavailable"),
    [state, setState] = useState<"idle" | "saving" | "success" | "error">(
      "idle",
    ),
    [error, setError] = useState(""),
    [blocksLoading, setBlocksLoading] = useState(true),
    [blocksError, setBlocksError] = useState(""),
    [successMessage, setSuccessMessage] = useState("");
  const saving = useRef(false);
  const active = useRef(true);
  const blockLoadRevision = useRef(0);
  const refreshBlocks = useCallback(async () => {
    const revision = ++blockLoadRevision.current;
    setBlocksLoading(true);
    setBlocksError("");
    try {
      const rows = await getAvailabilityBlocks();
      if (active.current && revision === blockLoadRevision.current) setBlocks(rows);
    } catch {
      if (active.current && revision === blockLoadRevision.current) {
        setBlocks([]);
        setBlocksError("Unavailable blocks could not be loaded. Refresh to check your saved blocks.");
      }
    } finally {
      if (active.current && revision === blockLoadRevision.current) setBlocksLoading(false);
    }
  }, []);
  const labels = {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
  };
  useEffect(() => {
    getAvailability()
      .then((value) => {
        if (!value) return;
        setTimezone(value.timezone);
        setBufferBefore(value.bufferBefore);
        setBufferAfter(value.bufferAfter);
        setNotice(value.minimumNotice);
        setHorizon(value.bookingHorizon);
        setHours(value.workingHours);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    active.current = true;
    void refreshBlocks();
    return () => { active.current = false; };
  }, [refreshBlocks]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving.current) return;
    saving.current = true;
    setState("saving");
    setError("");
    try {
      await manageAvailability({
        timezone,
        workingHours: hours,
        bufferBefore,
        bufferAfter,
        minimumNotice: notice,
        bookingHorizon: horizon,
      });
      setSuccessMessage("Availability saved.");
      setState("success");
    } catch (value) {
      setState("error");
      setError(
        value instanceof Error
          ? value.message.replaceAll("_", " ")
          : "Availability could not be saved.",
      );
    } finally { saving.current = false; }
  }
  async function addBlock() {
    if (saving.current || blocksLoading || blocksError) return;
    saving.current = true;
    setState("saving");
    setError("");
    try {
      await manageAvailabilityBlock({
        action: "CREATE",
        blockId: crypto.randomUUID(),
        startsAt: new Date(blockStart).toISOString(),
        endsAt: new Date(blockEnd).toISOString(),
        reason: blockReason,
      });
      await refreshBlocks();
      setSuccessMessage("Unavailable block added.");
      setState("success");
    } catch (value) {
      setState("error");
      setError(
        value instanceof Error
          ? value.message.replaceAll("_", " ")
          : "Unavailable block could not be saved.",
      );
    } finally { saving.current = false; }
  }
  async function removeBlock(blockId: string) {
    if (saving.current || blocksLoading || blocksError || !blocks.some(block => block.id === blockId)) return;
    saving.current = true;
    setState("saving");
    setError("");
    try {
      await manageAvailabilityBlock({action: "DELETE", blockId, startsAt: null, endsAt: null, reason: ""});
      await refreshBlocks();
      setSuccessMessage("Unavailable block removed.");
      setState("success");
    } catch (value) {
      setState("error");
      setError(value instanceof Error ? value.message.replaceAll("_", " ") : "Unavailable block could not be removed.");
    } finally { saving.current = false; }
  }
  return (
    <div className="record-modal-backdrop">
      <form className="record-modal availability-modal" onSubmit={save}>
        <header>
          <div>
            <span className="eyebrow">CALENDAR SETTINGS</span>
            <h2>Availability</h2>
          </div>
          <button type="button" aria-label="Close" onClick={onClose}>
            <X />
          </button>
        </header>
        <label>
          Timezone
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            required
          />
        </label>
        <div className="working-hours">
          <b>Working hours</b>
          {Object.entries(hours).map(([day, value]) => (
            <div key={day}>
              <span>{labels[day as keyof typeof labels] || day}</span>
              <input
                type="time"
                value={value?.start || ""}
                onChange={(e) =>
                  setHours({
                    ...hours,
                    [day]: {
                      start: e.target.value,
                      end: value?.end || "17:00",
                    },
                  })
                }
              />
              <span>to</span>
              <input
                type="time"
                value={value?.end || ""}
                onChange={(e) =>
                  setHours({
                    ...hours,
                    [day]: {
                      start: value?.start || "09:00",
                      end: e.target.value,
                    },
                  })
                }
              />
            </div>
          ))}
        </div>
        <label>
          Buffer before (minutes)
          <input
            type="number"
            min="0"
            max="1440"
            value={bufferBefore}
            onChange={(e) => setBufferBefore(Number(e.target.value))}
          />
        </label>
        <label>
          Buffer after (minutes)
          <input
            type="number"
            min="0"
            max="1440"
            value={bufferAfter}
            onChange={(e) => setBufferAfter(Number(e.target.value))}
          />
        </label>
        <label>
          Minimum notice (minutes)
          <input
            type="number"
            min="0"
            max="10080"
            value={notice}
            onChange={(e) => setNotice(Number(e.target.value))}
          />
        </label>
        <label>
          Booking horizon (days)
          <input
            type="number"
            min="1"
            max="365"
            value={horizon}
            onChange={(e) => setHorizon(Number(e.target.value))}
          />
        </label>
        <fieldset>
          <legend>Unavailable block</legend>
          <p>Block time on your CRM calendar and personal booking page.</p>
          <label>
            Starts
            <input
              type="datetime-local"
              value={blockStart}
              onChange={(e) => setBlockStart(e.target.value)}
            />
          </label>
          <label>
            Ends
            <input
              type="datetime-local"
              value={blockEnd}
              onChange={(e) => setBlockEnd(e.target.value)}
            />
          </label>
          <label>
            Reason
            <input
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            />
          </label>
          <button type="button" disabled={state === "saving" || blocksLoading || !!blocksError} onClick={() => void addBlock()}>
            Add unavailable block
          </button>
          <button type="button" disabled={state === "saving" || blocksLoading} onClick={() => void refreshBlocks()}>
            {blocksLoading ? "Loading unavailable blocks…" : "Refresh unavailable blocks"}
          </button>
          {blocksError && <p role="alert">{blocksError}</p>}
          {!blocksLoading && !blocksError && blocks.length === 0 && <p>No unavailable blocks saved.</p>}
          {blocks.map((block) => (
            <div key={block.id}>
            <small>
              {new Date(block.startsAt).toLocaleString()} –{" "}
              {new Date(block.endsAt).toLocaleString()} · {block.reason}
            </small>
            <button type="button" disabled={state === "saving" || blocksLoading} aria-label={`Remove unavailable block: ${block.reason}, ${new Date(block.startsAt).toLocaleString()} to ${new Date(block.endsAt).toLocaleString()}`} onClick={() => void removeBlock(block.id)}>Remove</button>
            </div>
          ))}
        </fieldset>
        {state === "error" && (
          <div className="composer-error" role="alert">
            {error}
          </div>
        )}
        {state === "success" && (
          <div className="composer-success" role="status">
            {successMessage}
          </div>
        )}
        <footer>
          <button type="button" onClick={onClose}>
            Close
          </button>
          <button className="primary-btn" disabled={state === "saving"}>
            {state === "saving" ? "Saving…" : "Save availability"}
          </button>
        </footer>
      </form>
    </div>
  );
}
